import { el, mount, unmount } from 'redom';

import application from 'application';

import { stringToSVG } from 'utilities';
import {
  displayElementAroundAnchorSticky
} from 'utilities/element-position';

import fileTypes from 'constants/file-types';

import DuplicateObject from 'generics/components/basic-text-editor-slate/toolbar-native/duplicate-object';
import PositionControlPanel from 'generics/components/basic-text-editor-slate/toolbar-native/position-control-panel';
import FloatedInput from './floated-input';
import AlignmentControls from './alignment-controls';
import ClipControls, { clipTypes } from './clip-controls';
import FitControls, { fitTypes } from './fit-controls';
import FilterPanel, { filterTypes } from './filter-panel';
import BrushControls from './brush-controls';
import FileSelectorPanelControl from './file-selector-panel-control';
import CropControl from './crop-control';

import altTextIconString from 'assets/images/node-menu-alt-text-input-icon.svg?raw';

import urlIconString from 'assets/images/link-toggle-icon.svg?raw';

import mediaUploadIconString from 'assets/images/media-upload-icon.svg?raw';

const mediaUploadIcon = stringToSVG(mediaUploadIconString);
const altTextIcon = stringToSVG(altTextIconString);
const urlIcon = stringToSVG(urlIconString);

class MediaEditControls {

  constructor(configuration, anchor) {

    this.readOnly = configuration.readOnly || false;
    this.editorMainReference = configuration.editorMainReference.current;
    this.mediaType = configuration.mediaType;
    this.altText = configuration.altText;
    this.url = configuration.url;
    this.disableAltText = configuration.disableAltText === true;
    this.disableAlignmentControls = configuration.disableAlignmentControls === true;
    this.allowGIFs = configuration.allowGIFs !== false;
    this.disableBrushControls = configuration.disableBrushControls !== false;

    this.onAltTextChange = configuration.onAltTextChange;
    this.onURLChange = configuration.onURLChange;
    this.onFile = configuration.onFile;
    this.onActive = configuration.onActive || (() => {});
    this.onInactive = configuration.onInactive || (() => {});

    this.anchor = anchor;

    this.alignmentControls = null;
    this.fitControls = null;
    this.altTextInputDisplayControl = null;
    this.urlInputDisplayControl = null;
    this.dynamicElementsContainer = null;
    this.filterPanel = null;

    this.staticElementsContainer = null;
    this.undoStickyness = null;

    this.el = el('.media-edit-controls-main', {}, [
      this.renderStaticElementsContainer(configuration),
      this.renderDynamicElementsContainer()
    ]);

  }

  positionOverlay() {

    const anchor = this.anchor;

    const element = this.el;
    const anchorElement = anchor;
    const viewportElement = void 0;
    const verticalOffset = 0;
    const horizontalOffset = 0;
    const startSide = 'bottom';
    const placeOnCenter = true;
    const positionableSides = void 0;
    const scrollableParent = this.editorMainReference;

    this.undoStickyness = displayElementAroundAnchorSticky(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides, scrollableParent);

  }

  onunmount() {

    this.tryUndoStickyness();
    this.onInactive();

  }

  onmount() {

    this.tryPositionOverlay();

  }

  tryPositionOverlay() {

    this.positionOverlay();

  }

  tryUndoStickyness() {

    if (this.undoStickyness === null) {
      return;
    }

    this.undoStickyness();

    this.el.style.position = '';
    this.el.style.top = '';
    this.el.style.left = '';
    this.el.style.height = '';

    this.undoStickyness = null;

  }

  reposition() {

    this.tryUndoStickyness();

    this.tryPositionOverlay();

  }

  tryRenderAlignmentControls(configuration) {

    if (this.canDisplayAlignmentControls() === false) {
      return;
    }

    this.alignmentControls = new AlignmentControls({
      alignment: configuration.alignment,
      onChange: configuration.onAlignmentChange
    });

    return this.alignmentControls;

  }

  tryRenderAltTextInputDisplayControl() {

    if (this.mediaType !== fileTypes.IMAGE) {
      return;
    }

    if (this.disableAltText === true) {
      return;
    }

    this.altTextInputDisplayControl = el('button.alt-text-input-display-control', {
      class: 'inline-toolbar-button',
      onclick: this.handleAltTextInputDisplayControlClick.bind(this)
    }, altTextIcon.cloneNode(true));

    return el('x-tooltip', {
      text: 'Update alt text'
    }, [this.altTextInputDisplayControl]);

  }

  renderURLInputDisplayControl() {

    this.urlInputDisplayControl = el('button.media-url-input-display-control', {
      class: 'inline-toolbar-button',
      onclick: this.handleURLInputDisplayControlClick.bind(this)
    }, urlIcon.cloneNode(true));

    return this.urlInputDisplayControl;

  }

  renderUploadControl() {

    this.uploadControl = el('button.media-upload-control', {
      class: 'inline-toolbar-button',
      onclick: this.handleUploadControlClick.bind(this)
    }, mediaUploadIcon.cloneNode(true));

    return this.uploadControl;

  }

  renderFileSelectorPanelControl() {

    this.fileSelectorPanelControl = new FileSelectorPanelControl({
      allowGIFs: this.allowGIFs,
      url: this.url,
      onURLChange: this.onURLChange,
      onFile: this.onFile
    });

    return this.fileSelectorPanelControl;

  }

  renderSecondButtonsSection() {

    if (this.readOnly === true) {
      return;
    }

    return el('.second-buttons-section', {
      class: 'toolbar-section'
    }, [
      this.tryRenderAltTextInputDisplayControl(),
      this.renderFileSelectorPanelControl()
    ]);

  }

  tryRenderClipControls(configuration) {

    this.clipControls = new ClipControls({
      clip: configuration.clip,
      onChange: configuration.onClipChange
    });

    return this.clipControls;

  }

  tryRenderFitControls(configuration) {

    if (typeof configuration.onFitChange !== 'function') {
      return;
    }

    if (this.canDisplayFitControls() === false) {
      return;
    }

    this.fitControls = new FitControls({
      fit: configuration.fit,
      onChange: configuration.onFitChange
    });

    return this.fitControls;

  }

  tryRenderPositionControlPanel(configuration) {

    if (configuration.displayPositionControlPanel !== true) {
      return;
    }

    this.positionControlPanel = new PositionControlPanel(configuration);

    return this.positionControlPanel;

  }

  tryRenderFilterPanel(configuration) {

    if (configuration.filter === void 0) {
      return;
    }

    this.filterPanel = new FilterPanel({
      filter: configuration.filter,
      onActive: configuration.onActive,
      onInactive: configuration.onInactive
    });

    return this.filterPanel;

  }

  tryRenderBrushControls(configuration) {

    if (this.disableBrushControls === true) {
      return;
    }

    this.brushControls = new BrushControls({
      canvas: configuration.canvas
    });

    return this.brushControls;

  }

  tryRenderDuplicateObject(configuration) {

    if (typeof configuration.onDuplicate !== 'function') {
      return;
    }

    return el('.toolbar-section', [
      new DuplicateObject(configuration)
    ]);

  }

  tryRenderCropControl(configuration) {

    if (typeof configuration.onCrop !== 'function') {
      return;
    }

    return el('.toolbar-section', [
      new CropControl(configuration)
    ]);

  }

  renderStaticElementsContainer(configuration) {

    this.staticElementsContainer = el('.static-contents-container', [
      this.tryRenderAlignmentControls(configuration),
      this.tryRenderClipControls(configuration),
      this.tryRenderFitControls(configuration),
      this.tryRenderFilterPanel(configuration),
      this.tryRenderBrushControls(configuration),
      this.renderSecondButtonsSection(),
      this.tryRenderPositionControlPanel(configuration),
      this.tryRenderDuplicateObject(configuration),
      this.tryRenderCropControl(configuration)
    ]);

    return this.staticElementsContainer;

  }

  renderDynamicElementsContainer() {

    this.dynamicElementsContainer = el('.dynamic-elements-container');

    return this.dynamicElementsContainer;

  }

  handleAltTextCompletedEvent(altText, input) {

    unmount(this.dynamicElementsContainer, input);

    this.onAltTextChange(altText);

  }

  handleAltTextCancelledEvent(input) {

    unmount(this.dynamicElementsContainer, input);

  }

  displayAltTextInput() {

    const altTextInput = new FloatedInput({
      value: this.altText,
      placeholder: 'Alt text...',
      onComplete: this.handleAltTextCompletedEvent.bind(this),
      onCancel: this.handleAltTextCancelledEvent.bind(this),
      anchor: this.altTextInputDisplayControl,
      viewport: this.editorMainReference
    });

    mount(this.dynamicElementsContainer, altTextInput);

  }

  handleAltTextInputDisplayControlClick() {

    this.displayAltTextInput();

  }

  handleURLCompletedEvent(url, input) {

    unmount(this.dynamicElementsContainer, input);

    this.onURLChange(url);

  }

  handleURLCancelledEvent(input) {

    unmount(this.dynamicElementsContainer, input);

  }

  displayURLInput() {

    const urlInput = new FloatedInput({
      value: this.url,
      placeholder: 'https://',
      onComplete: this.handleURLCompletedEvent.bind(this),
      onCancel: this.handleURLCancelledEvent.bind(this),
      anchor: this.urlInputDisplayControl,
      viewport: this.editorMainReference
    });

    mount(this.dynamicElementsContainer, urlInput);

  }

  handleURLInputDisplayControlClick() {

    this.displayURLInput();

  }

  canDisplayAlignmentControls() {

    return this.mediaType !== fileTypes.DOCUMENT && this.disableAlignmentControls === false;

  }

  canDisplayFitControls() {

    return this.mediaType !== fileTypes.DOCUMENT;

  }

  async uploadFile() {

    const response = await application.domUtility.askAnyTypeFile();

    if (response.cancelled === true) {
      return;
    }

    this.onFile(response.file);

  }

  handleUploadControlClick() {

    this.uploadFile();

  }

  displayOrUpdateAlignmentControls(configuration) {

    let alignmentControls = this.alignmentControls;

    if (alignmentControls === null) {

      alignmentControls = this.tryRenderAlignmentControls(configuration);

      this.staticElementsContainer.prepend(alignmentControls.el);

    }

    this.alignmentControls = alignmentControls;

    this.alignmentControls.update(configuration);

  }

  tryRemoveAlignmentControls() {

    if (this.alignmentControls === null) {
      return;
    }

    unmount(this.staticElementsContainer, this.alignmentControls);

    this.alignmentControls = null;

  }

  tryUpdateFitControls(configuration) {

    if (this.fitControls === null) {
      return;
    }

    this.fitControls.update(configuration);

  }

  update(configuration, anchor) {

    this.mediaType = configuration.mediaType;
    this.altText = configuration.altText;
    this.url = configuration.url;

    this.onAltTextChange = configuration.onAltTextChange;
    this.onURLChange = configuration.onURLChange;
    this.onFile = configuration.onFile;

    this.anchor = anchor;

    if (this.canDisplayAlignmentControls() === true) {
      this.displayOrUpdateAlignmentControls(configuration);
    } else {
      this.tryRemoveAlignmentControls(configuration);
    }

    this.tryUpdateFitControls(configuration);

    this.reposition();

  }

}

export {
  clipTypes,
  fitTypes,
  filterTypes
};

export default MediaEditControls;